/* eslint-disable consistent-return */
import { useEffect } from 'react';
import type { FC } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Avatar, Box, Drawer, Typography, Link, alpha } from '@mui/material';
import type { Theme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import useAuth from 'src/hooks/useAuth';
import Logo from 'src/components/Logo';
import NavSection from 'src/components/NavSection';
import Scrollbar from 'src/components/Scrollbar';
import { PATH_DASHBOARD, PATH_ERRORS } from 'src/routes/paths';
import { usePermissionsQuery } from 'src/graphql/idr/resource/queries/permissions.generated';

interface DashboardSidebarProps {
  onMobileClose: () => void;
  openMobile: boolean;
}

const DashboardSidebar: FC<DashboardSidebarProps> = (props) => {
  const { onMobileClose, openMobile } = props;
  const location = useLocation();
  // const navigate = useNavigate();
  const { user } = useAuth();
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const {
    data: {
      resourceQueries: {
        permissions: {
          listDto: { items },
        },
      },
    },
  } = usePermissionsQuery(
    { data: {} },
    {
      initialData: {
        resourceQueries: { permissions: { listDto: { items: [] } } },
      },
    }
  );

  let root = [];
  // TODO: HANDLE 404 PAGES
  // const EXCLUDED_PATH = ['/dashboard/overview', '/dashboard/profile'];

  // if (
  //   !EXCLUDED_PATH.includes(location.pathname) &&
  //   items.length > 0 &&
  //   !items.some((item) => item.resource.value === location.pathname)
  // ) {
  //   navigate(PATH_ERRORS.notFound);
  // }

  items.forEach((item) => {
    if (!item.resource.parentId) root.push(item.resource);
  });

  if (root.length) {
    root = root.map((rootItem) => {
      const children = [];
      items.forEach((item) => {
        if (item.resource.parentId === rootItem.id)
          children.push(item.resource);
      });
      return { ...rootItem, children };
    });
  }

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        top: '104px !important',
      }}
    >
      <Scrollbar
        style={{ display: 'flex', flexDirection: 'column' }}
        options={{ suppressScrollX: true }}
      >
        <Box
          sx={{
            display: {
              lg: 'none',
              xs: 'flex',
            },
            justifyContent: 'center',
            p: 2,
          }}
        >
          <RouterLink to={PATH_DASHBOARD.overview}>
            <Logo
              sx={{
                height: 40,
                width: 40,
              }}
            />
          </RouterLink>
        </Box>
        <Box sx={{ p: 2, pb: 0 }}>
          <Link
            to={PATH_DASHBOARD.profile}
            component={RouterLink}
            sx={{ textDecoration: 'unset' }}
          >
            <Box
              sx={{
                alignItems: 'center',
                backgroundColor: alpha('#fff', 0.15),
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                p: 2,
              }}
            >
              <Box sx={{ width: '100%', display: 'flex' }}>
                <Avatar
                  src={user?.avatarUrl || '/static/user.svg'}
                  sx={{
                    cursor: 'pointer',
                    height: 48,
                    width: 48,
                    mb: 1,
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 7,
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    ml: 1,
                  }}
                >
                  <Typography color="white" variant="subtitle2">
                    {`نقش: ${user.roleDtos.map((role) => role.name)}`}
                  </Typography>
                  <Typography color="white" variant="subtitle2">
                    {user?.fullName}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Link>
        </Box>
        <Box sx={{ p: 4, pt: 0 }}>
          <NavSection pathname={location.pathname} items={root} />
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box>
          <Typography color="primary.contrastText" fontSize="10px">
            &copy; تمامی حقوق این سایت متعلق به هیئت آنلاین می‌باشد.
          </Typography>
        </Box>
      </Scrollbar>
    </Box>
  );

  if (lgUp) {
    return (
      <Drawer
        anchor="left"
        open
        PaperProps={{
          sx: {
            backgroundColor: 'secondary.main',
            height: 'calc(100% - 82px) !important',
            top: '82px !Important',
            width: 240,
          },
        }}
        variant="permanent"
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="left"
      onClose={onMobileClose}
      open={openMobile}
      PaperProps={{
        sx: {
          backgroundColor: 'secondary.main',
          width: 240,
        },
      }}
      variant="temporary"
    >
      {content}
    </Drawer>
  );
};

DashboardSidebar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
};

export default DashboardSidebar;
